import { useState } from 'react'
import { FileIcon, Loader2, ZoomIn, ZoomOut } from 'lucide-react'
import { withAttachmentDisposition } from '@klear/upload/file-url'
import { useFileDownload } from '@klear/ui/lib/download-file'
import { Slider } from '@klear/ui/components/slider'
import constant from 'lodash/constant'
import isNumber from 'lodash/isNumber'
import {
  OrderEventFilePreviewBreadcrumb,
  OrderEventFilePreviewEventDetails
} from '@klear/ui/components/order-event-file-preview-sidebar'
import type {
  OrderEventFilePreviewBreadcrumbItem,
  OrderEventFilePreviewContext
} from '@klear/order-event/file-preview-display'

interface ImgPreviewProps {
  context: OrderEventFilePreviewContext
  fileUrl: string
  fileName: string
  getEventUrl?: (item: OrderEventFilePreviewBreadcrumbItem) => string
}

export default function ImgPreview({ context, fileUrl, fileName, getEventUrl = constant('') }: ImgPreviewProps) {
  const [error, setError] = useState<Error | null>(null)
  const [scale, setScale] = useState<number>(1.0)
  const { startDownload, isDownloading } = useFileDownload()

  if (!fileUrl) {
    return <div className="text-center text-red-500 py-10">No Image URL provided</div>
  }

  return (
    <div className="flex flex-col">
      <h1 className="flex items-center text-2xl font-bold mb-4">
        <FileIcon className="w-4 h-4 mr-2" />
        <span>{fileName}</span>
        {fileUrl && (
          <button
            type="button"
            onClick={() => startDownload(withAttachmentDisposition(fileUrl), fileName)}
            disabled={isDownloading}
            className="ml-4 inline-flex items-center gap-2 px-3 py-1 bg-muted rounded hover:bg-muted/80 disabled:opacity-60"
          >
            <span>Download</span>
            {isDownloading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4"
                />
              </svg>
            )}
          </button>
        )}
      </h1>
      <OrderEventFilePreviewBreadcrumb context={context} getEventUrl={getEventUrl} />
      <div className="flex gap-4">
        <div className="w-1/4" style={{ maxHeight: '75vh' }}>
          <OrderEventFilePreviewEventDetails context={context} />
        </div>
        <div className="w-3/4">
          <div className="border rounded-lg p-4 bg-card shadow-sm w-full h-[70vh] overflow-auto">
            <img
              src={fileUrl}
              alt={fileName}
              className="object-contain w-full h-full transition-transform duration-200 origin-center"
              style={{ transform: `scale(${scale})` }}
              onError={(e) => setError(new Error((e.target as HTMLImageElement).alt || 'Failed to load image'))}
            />
          </div>

          <div className="mt-4 flex items-center gap-3">
            <ZoomOut className="w-4 h-4 text-muted-foreground" />
            <Slider
              min={0.5}
              max={3}
              step={0.05}
              value={[scale]}
              onValueChange={([val]) => setScale(isNumber(val) ? val : scale)}
              aria-label="Zoom"
            />
            <ZoomIn className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm w-12 text-center font-medium">{Math.round(scale * 100)}%</span>
            <button
              onClick={() => setScale(1)}
              className="text-xs px-2 py-1 bg-muted hover:bg-muted/80 rounded transition-colors"
            >
              Reset
            </button>
          </div>

          {error && <div className="text-red-500 mt-2">Failed to load Image: {error.message}</div>}
        </div>
      </div>
    </div>
  )
}
